/**
 * Time for Office OS.
 *
 * Two hard problems this module exists to solve, both from docs/02 §4:
 *
 *  1. Egypt reinstated DST in 2023. Cheap Android tablets ship tzdata <= 2022a,
 *     which predates that rule, so `Africa/Cairo` resolves to UTC+2 year-round
 *     and every prayer time reads an hour early from late April to late October.
 *     We therefore NEVER ask the platform what the Cairo offset is. We compute it.
 *
 *  2. The space has no office hours, so midnight is the wrong day boundary.
 *     A visit from 21:00 to 04:00 is ONE session. Everything date-keyed uses a
 *     configurable rollover hour (05:00) instead of midnight.
 *
 * All arithmetic is done on UTC epoch milliseconds using UTC accessors only.
 * Nothing here reads the host machine's timezone, so results are identical on
 * the tablet, the phones, the server and CI.
 */

/** Minutes east of UTC. Egypt is +02:00 standard, +03:00 daylight. */
export type CairoOffsetMinutes = 120 | 180

/** A calendar day in the office's own reckoning: 'YYYY-MM-DD'. */
export type DayKey = string & { readonly __brand: 'DayKey' }

/** An office week, keyed by the DayKey of its Saturday: 'YYYY-MM-DD'. */
export type WeekKey = string & { readonly __brand: 'WeekKey' }

export const CAIRO_STANDARD_OFFSET: CairoOffsetMinutes = 120
export const CAIRO_DAYLIGHT_OFFSET: CairoOffsetMinutes = 180

/** The hour at which the office day rolls over. Confirmed 05:00 (docs/01). */
export const DEFAULT_ROLLOVER_HOUR = 5

/**
 * The office week starts Saturday, matching the Egyptian working week.
 * JS getUTCDay(): 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
 */
export const WEEK_START_DOW = 6

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 3_600_000
const MS_PER_DAY = 86_400_000

/** First year Egypt's reinstated-DST rule applies. Before this, no DST. */
export const EGYPT_DST_FIRST_YEAR = 2023

/**
 * UTC ms for the last occurrence of `dow` in the given month, at 00:00 UTC.
 * month is 0-indexed (0 = January), matching Date.UTC.
 */
function lastWeekdayOfMonthUtc(year: number, month: number, dow: number): number {
  // Day 0 of the *next* month is the last day of this month.
  const lastDay = Date.UTC(year, month + 1, 0)
  const lastDow = new Date(lastDay).getUTCDay()
  const delta = (lastDow - dow + 7) % 7
  return lastDay - delta * MS_PER_DAY
}

/**
 * Egypt's DST window, as UTC instants.
 *
 * tzdata rule (2023 onward):
 *   Rule Egypt  2023  max  -  Apr  lastFri  0:00   1:00  S
 *   Rule Egypt  2023  max  -  Oct  lastThu  24:00  0     -
 *
 * Start: last Friday of April at 00:00 LOCAL STANDARD time (+02:00),
 *        so the UTC instant is 22:00 on the preceding Thursday.
 * End:   last Thursday of October at 24:00 LOCAL DAYLIGHT time (+03:00),
 *        i.e. Friday 00:00 daylight, so the UTC instant is 21:00 Thursday.
 */
export function egyptDstWindowUtc(year: number): { startUtc: number; endUtc: number } | null {
  if (year < EGYPT_DST_FIRST_YEAR) return null
  const aprLastFri = lastWeekdayOfMonthUtc(year, 3, 5) // April, Friday
  const octLastThu = lastWeekdayOfMonthUtc(year, 9, 4) // October, Thursday
  return {
    startUtc: aprLastFri - CAIRO_STANDARD_OFFSET * MS_PER_MINUTE,
    endUtc: octLastThu + MS_PER_DAY - CAIRO_DAYLIGHT_OFFSET * MS_PER_MINUTE,
  }
}

/** The Cairo UTC offset in effect at a given UTC instant. Never consults the platform. */
export function cairoOffsetMinutes(utcMs: number): CairoOffsetMinutes {
  const year = new Date(utcMs).getUTCFullYear()
  // A December instant can never be in DST; an early-January one belongs to the
  // previous year's window, which has already closed. Checking the instant's own
  // UTC year is therefore sufficient.
  const w = egyptDstWindowUtc(year)
  if (!w) return CAIRO_STANDARD_OFFSET
  return utcMs >= w.startUtc && utcMs < w.endUtc ? CAIRO_DAYLIGHT_OFFSET : CAIRO_STANDARD_OFFSET
}

export interface CairoParts {
  year: number
  month: number // 1-12
  day: number // 1-31
  hour: number // 0-23
  minute: number
  second: number
  /** 0=Sun … 6=Sat */
  weekday: number
  offsetMinutes: CairoOffsetMinutes
}

/** Decompose a UTC instant into Cairo wall-clock parts. */
export function cairoParts(utcMs: number): CairoParts {
  const offset = cairoOffsetMinutes(utcMs)
  const shifted = new Date(utcMs + offset * MS_PER_MINUTE)
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
    weekday: shifted.getUTCDay(),
    offsetMinutes: offset,
  }
}

const p2 = (n: number) => String(n).padStart(2, '0')

/** 'HH:MM' in Cairo wall-clock time. */
export function formatCairoTime(utcMs: number): string {
  const p = cairoParts(utcMs)
  return `${p2(p.hour)}:${p2(p.minute)}`
}

/**
 * '7:15 pm' — Cairo wall-clock time for DISPLAY.
 *
 * Separate from {@link formatCairoTime}, which stays 24-hour on purpose: that
 * one is a DATA format. `prayerTimesFor` returns its six instants through it,
 * settings store times through it, and both are compared and sorted as strings
 * — '09:05' < '14:20' only holds while the hour is zero-padded and 24-hour.
 * Making that function 12-hour to change what a screen shows would silently
 * reorder the prayer table.
 *
 * Lower-case 'am'/'pm' rather than Intl's locale-dependent output, and built
 * from `cairoParts` like everything else here, because a tablet with tzdata
 * older than 2023a believes Africa/Cairo is UTC+2 all summer and Intl would
 * print every time an hour early (docs/02 §4).
 */
export function formatCairoTime12(utcMs: number): string {
  const p = cairoParts(utcMs)
  const suffix = p.hour < 12 ? 'am' : 'pm'
  // 0 -> 12 (midnight) and 12 -> 12 (noon); every other hour is h % 12.
  const h = p.hour % 12 === 0 ? 12 : p.hour % 12
  return `${h}:${p2(p.minute)} ${suffix}`
}

/** 'YYYY-MM-DD' in Cairo wall-clock time (calendar date, NOT the rollover day). */
export function formatCairoDate(utcMs: number): string {
  const p = cairoParts(utcMs)
  return `${p.year}-${p2(p.month)}-${p2(p.day)}`
}

/**
 * 'YYYY-MM-DD 1:00 pm' — a stamp a person reads, in Cairo wall-clock time.
 *
 * Both callers (the settings audit log, the restore confirmation) print this at
 * somebody; neither sorts or compares it. The 24-hour {@link formatCairoDateTime}
 * stays for anything that does.
 */
export function formatCairoDateTime12(utcMs: number): string {
  return `${formatCairoDate(utcMs)} ${formatCairoTime12(utcMs)}`
}

/** 'YYYY-MM-DD HH:MM' in Cairo wall-clock time. */
export function formatCairoDateTime(utcMs: number): string {
  return `${formatCairoDate(utcMs)} ${formatCairoTime(utcMs)}`
}

/**
 * Which office day a UTC instant belongs to.
 * With rolloverHour = 5, an instant at 03:00 Cairo belongs to the PREVIOUS
 * calendar date, because the office day has not rolled over yet.
 */
export function rolloverDayOf(utcMs: number, rolloverHour: number = DEFAULT_ROLLOVER_HOUR): DayKey {
  // Decide in LOCAL wall-clock terms, never by shifting the UTC instant.
  // Shifting by -5h and then formatting is wrong whenever a DST transition
  // falls inside that window: the offset at the shifted instant differs from
  // the offset at the real one, and 05:00 on the spring transition day gets
  // misfiled into the previous office day. Found by property test.
  const p = cairoParts(utcMs)
  const local = `${p.year}-${p2(p.month)}-${p2(p.day)}` as DayKey
  return p.hour >= rolloverHour ? local : addDays(local, -1)
}

/**
 * The UTC instant of a Cairo wall-clock time, handed in as a "naive" value —
 * the wall-clock fields packed with `Date.UTC` as though Cairo were UTC.
 *
 * A naive local time is NOT a function of one offset. Twice a year it is either
 * ambiguous or impossible, and the answer has to agree with `rolloverDayOf`,
 * which reads the wall clock directly. So both candidates are tried and only
 * the one whose own offset is actually in effect at it is accepted:
 *
 *  - AMBIGUOUS (autumn). The clock goes back at 24:00 daylight, so 23:00-24:00
 *    is on the wall twice. Both candidates are valid; the daylight one is
 *    EARLIER and wins, because `rolloverDayOf` starts the new office day at the
 *    first time the wall clock reads the rollover hour, not the second.
 *  - NONEXISTENT (spring). The clock jumps 00:00 -> 01:00, so a rollover hour
 *    of 0 names a wall-clock time that never happens. Neither candidate is
 *    valid and the fallthrough lands exactly on the transition instant, which
 *    is the first moment the wall clock is past the rollover hour.
 *
 * At the office's 05:00 rollover exactly one candidate is ever valid and this
 * is the obvious subtraction. It is the configurable hours — `dayRolloverHour`
 * is settable to anything in 0..23 — that need the rest of it.
 */
function cairoInstantOfLocal(localNaive: number): number {
  const daylight = localNaive - CAIRO_DAYLIGHT_OFFSET * MS_PER_MINUTE
  if (cairoOffsetMinutes(daylight) === CAIRO_DAYLIGHT_OFFSET) return daylight
  return localNaive - CAIRO_STANDARD_OFFSET * MS_PER_MINUTE
}

/**
 * The UTC half-open interval [start, end) covered by an office day.
 * Correctly handles the two days a year when the Cairo offset changes mid-day:
 * the DST transitions happen at 00:00 and 24:00 local, so a 05:00-rollover day
 * containing a transition is 23 or 25 hours long, and this computes that.
 *
 * Contiguous by construction — a day ends where the next one begins — and it
 * agrees with `rolloverDayOf` for EVERY rollover hour, including the two that
 * name the transition itself (0 in spring, 23 in autumn).
 */
export function rolloverDayBounds(
  day: DayKey,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): { startUtc: number; endUtc: number } {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return {
    startUtc: cairoInstantOfLocal(Date.UTC(y, m - 1, d, rolloverHour, 0, 0, 0)),
    endUtc: cairoInstantOfLocal(Date.UTC(y, m - 1, d + 1, rolloverHour, 0, 0, 0)),
  }
}

/** Step a DayKey by whole days. */
export function addDays(day: DayKey, n: number): DayKey {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  const t = Date.UTC(y, m - 1, d + n)
  const dt = new Date(t)
  return `${dt.getUTCFullYear()}-${p2(dt.getUTCMonth() + 1)}-${p2(dt.getUTCDate())}` as DayKey
}

/** Whole days between two DayKeys (b - a). */
export function daysBetween(a: DayKey, b: DayKey): number {
  const pa = a.split('-').map(Number) as [number, number, number]
  const pb = b.split('-').map(Number) as [number, number, number]
  return Math.round(
    (Date.UTC(pb[0], pb[1] - 1, pb[2]) - Date.UTC(pa[0], pa[1] - 1, pa[2])) / MS_PER_DAY,
  )
}

/** Day of week for a DayKey. 0=Sun … 6=Sat. */
export function dayOfWeek(day: DayKey): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

/** The office week (Saturday-start) containing a day, keyed by its Saturday. */
export function weekOf(day: DayKey): WeekKey {
  const back = (dayOfWeek(day) - WEEK_START_DOW + 7) % 7
  return addDays(day, -back) as unknown as WeekKey
}

/** Every DayKey in an office week, Saturday first. */
export function daysOfWeek(week: WeekKey): DayKey[] {
  const start = week as unknown as DayKey
  return Array.from({ length: 7 }, (_, i) => addDays(start, i))
}

/**
 * A clock whose absolute time is trusted.
 *
 * On an offline tablet the system clock is set by hand and, if the device's
 * tzdata is stale, Android stores an epoch that is a full hour wrong. We
 * therefore never read `Date.now()` directly in domain code: we read it through
 * a clock carrying a correction delta synced from a phone (docs/02 §4).
 */
export interface TrustedClock {
  /** Corrected UTC epoch milliseconds. */
  now(): number
  /** ms of correction applied to the device clock. 0 when the device is trusted. */
  skewMs(): number
  /** When the correction was last refreshed, in corrected UTC ms. Null if never. */
  lastSyncedAt(): number | null
}

export function makeTrustedClock(opts: {
  deviceNow?: () => number
  skewMs?: number
  lastSyncedAt?: number | null
}): TrustedClock {
  const deviceNow = opts.deviceNow ?? (() => Date.now())
  const skew = opts.skewMs ?? 0
  const synced = opts.lastSyncedAt ?? null
  return {
    now: () => deviceNow() + skew,
    skewMs: () => skew,
    lastSyncedAt: () => synced,
  }
}

/** How stale the clock correction is, in ms. Infinity if never synced. */
export function clockStalenessMs(clock: TrustedClock): number {
  const last = clock.lastSyncedAt()
  return last === null ? Number.POSITIVE_INFINITY : clock.now() - last
}

/**
 * Build-time known-answer table (docs/02 §4).
 *
 * Asserted at boot. Catches a bad edit to our own DST constant, which comparing
 * against the platform's Intl cannot do — by design we ignore Intl when the two
 * disagree, so Intl is not a usable oracle at runtime.
 */
export const KNOWN_OFFSETS: ReadonlyArray<{ iso: string; expected: CairoOffsetMinutes }> = [
  { iso: '2026-01-15T12:00:00Z', expected: 120 },
  { iso: '2026-05-01T12:00:00Z', expected: 180 },
  { iso: '2026-07-01T12:00:00Z', expected: 180 },
  { iso: '2026-11-15T12:00:00Z', expected: 120 },
  { iso: '2027-01-01T12:00:00Z', expected: 120 },
  { iso: '2027-07-01T12:00:00Z', expected: 180 },
  { iso: '2022-07-01T12:00:00Z', expected: 120 }, // pre-2023: no DST
]

export function selfCheckClock(): { ok: boolean; failures: string[] } {
  const failures: string[] = []
  for (const { iso, expected } of KNOWN_OFFSETS) {
    const actual = cairoOffsetMinutes(Date.parse(iso))
    if (actual !== expected) failures.push(`${iso}: expected ${expected}, got ${actual}`)
  }
  return { ok: failures.length === 0, failures }
}
